import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import HandymanIcon from '@mui/icons-material/Handyman';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DataTable from '../../components/common/DataTable';
import { useQuotes, useSendQuote } from '../../hooks/useApi';
import { api } from '../../services/api';
import type { Quote, QuoteStatus, QuoteStop, QuoteType } from '../../types';

function daysSince(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

const statusConfig: Record<QuoteStatus, { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  DRAFT: { label: 'Kladde', color: 'default' },
  SENT: { label: 'Sendt', color: 'info' },
  ACCEPTED: { label: 'Accepteret', color: 'success' },
  REJECTED: { label: 'Afvist', color: 'error' },
  CONVERTED: { label: 'Konverteret', color: 'success' },
  EXPIRED: { label: 'Udløbet', color: 'warning' },
};

const quoteTypeIcons: Record<QuoteType, JSX.Element> = {
  TRANSPORT: <LocalShippingIcon sx={{ fontSize: 14 }} />,
  CONTAINER: <Inventory2Icon sx={{ fontSize: 14 }} />,
  SERVICE: <HandymanIcon sx={{ fontSize: 14 }} />,
  WASTE: <DeleteOutlineIcon sx={{ fontSize: 14 }} />,
  RECURRING: <RepeatIcon sx={{ fontSize: 14 }} />,
};

const quoteTypeLabels: Record<QuoteType, string> = {
  TRANSPORT: 'Transport',
  CONTAINER: 'Container',
  SERVICE: 'Service',
  WASTE: 'Affald',
  RECURRING: 'Tilbagevendende',
};

function fmtCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount);
}

function fmtDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function getCity(stops: QuoteStop[]): string {
  if (!stops?.length) return '—';
  const s = stops[0];
  return s.place?.city || s.place?.postcode || '—';
}

function routeSummary(row: Quote): string {
  if (row.useNewAddressModel) {
    const addr = row.quoteAddress;
    const center = addr?.placeName || addr?.city || addr?.postcode || 'Tilbudsadr.';
    const pickupCount = (row.pickupLines || []).length;
    const dropoffCount = (row.dropoffLines || []).length;
    const parts: string[] = [];
    if (pickupCount > 0) parts.push(`${pickupCount} afh.`);
    if (dropoffCount > 0) parts.push(`${dropoffCount} bortk.`);
    return parts.length ? `${center} (${parts.join(', ')})` : center;
  }
  const from = getCity(row.pickups);
  const to = getCity(row.dropoffs);
  return `${from} → ${to}`;
}

// Combined status + age chip
function StatusChip({ row }: { row: Quote }) {
  const cfg = statusConfig[row.status as QuoteStatus] || { label: row.status, color: 'default' as const };
  let label: string = cfg.label;
  let color: typeof cfg.color = cfg.color;

  if (row.status === 'SENT' && row.sentAt) {
    const days = daysSince(row.sentAt);
    if (days != null) {
      label = days === 0 ? 'Sendt i dag' : days === 1 ? 'Sendt 1 d' : `Sendt ${days} d`;
      if (days >= 14) color = 'error';
      else if (days >= 7) color = 'warning';
    }
  }

  return (
    <Chip
      label={label}
      color={color}
      size="small"
      variant={row.status === 'CONVERTED' ? 'outlined' : 'filled'}
    />
  );
}

export default function QuoteList() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'draft' | 'sent' | 'followup' | 'accepted' | 'rejected' | 'converted'>('all');
  const { data: allQuotes = [], isLoading } = useQuotes();
  const sendQuote = useSendQuote();
  const [sendConfirm, setSendConfirm] = useState<Quote | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const handleSend = async () => {
    if (!sendConfirm) return;
    try {
      await sendQuote.mutateAsync(sendConfirm.id);
      setSnackbar({ open: true, message: `Tilbud #${sendConfirm.quoteNumber} sendt til ${sendConfirm.customerEmail}`, severity: 'success' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke sende tilbud';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSendConfirm(null);
    }
  };

  const quotes = useMemo(() => {
    switch (filter) {
      case 'all': return allQuotes;
      case 'draft': return allQuotes.filter(q => q.status === 'DRAFT');
      case 'sent': return allQuotes.filter(q => q.status === 'SENT');
      case 'followup': return allQuotes.filter(q => {
        if (q.status !== 'SENT') return false;
        const days = daysSince(q.sentAt);
        return days != null && days >= 7;
      });
      case 'accepted': return allQuotes.filter(q => q.status === 'ACCEPTED');
      case 'rejected': return allQuotes.filter(q => q.status === 'REJECTED');
      case 'converted': return allQuotes.filter(q => q.status === 'CONVERTED');
      default: return allQuotes;
    }
  }, [allQuotes, filter]);

  // KPI counts
  const counts = useMemo(() => ({
    all: allQuotes.length,
    draft: allQuotes.filter(q => q.status === 'DRAFT').length,
    sent: allQuotes.filter(q => q.status === 'SENT').length,
    followup: allQuotes.filter(q => {
      if (q.status !== 'SENT') return false;
      const days = daysSince(q.sentAt);
      return days != null && days >= 7;
    }).length,
    accepted: allQuotes.filter(q => q.status === 'ACCEPTED').length,
    rejected: allQuotes.filter(q => q.status === 'REJECTED').length,
    converted: allQuotes.filter(q => q.status === 'CONVERTED').length,
    pipelineValue: allQuotes
      .filter(q => q.status === 'SENT')
      .reduce((sum, q) => sum + q.totalAmount, 0),
  }), [allQuotes]);

  const columns = [
    {
      id: 'quoteNumber' as const,
      label: '#',
      minWidth: 60,
      format: (value: unknown) => <Typography variant="body2" fontWeight={600}>#{value as number}</Typography>,
    },
    {
      id: 'status' as const,
      label: 'Status',
      minWidth: 110,
      format: (_: unknown, row: Quote) => <StatusChip row={row} />,
    },
    {
      id: 'quoteType' as const,
      label: 'Type',
      minWidth: 60,
      sortable: false,
      format: (value: unknown) => {
        const t = (value as QuoteType) || 'TRANSPORT';
        return (
          <Tooltip title={quoteTypeLabels[t]}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}>
              {quoteTypeIcons[t]}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      id: 'customerName' as const,
      label: 'Kunde',
      minWidth: 160,
      format: (value: unknown, row: Quote) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>{value as string}</Typography>
          {row.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
              {row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'pickups' as const,
      label: 'Rute',
      minWidth: 140,
      sortable: false,
      format: (_: unknown, row: Quote) => (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {routeSummary(row)}
        </Typography>
      ),
    },
    {
      id: 'totalAmount' as const,
      label: 'Beløb',
      minWidth: 100,
      align: 'right' as const,
      format: (_: unknown, row: Quote) => (
        <Typography variant="body2" fontWeight={600}>
          {fmtCurrency(row.totalAmount, row.currency)}
        </Typography>
      ),
    },
    {
      id: 'createdAt' as const,
      label: 'Oprettet',
      minWidth: 80,
      format: (value: unknown) => (
        <Typography variant="caption" color="text.secondary">{fmtDate(value as string)}</Typography>
      ),
    },
    {
      id: 'createdByUserId' as const,
      label: 'Oprettet af',
      minWidth: 110,
      sortable: false,
      format: (_: unknown, row: Quote) => (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {row.createdBy?.name || row.createdBy?.email || '—'}
        </Typography>
      ),
    },
    {
      id: 'id' as const,
      label: 'Handling',
      minWidth: 100,
      sortable: false,
      format: (_: unknown, row: Quote) => {
        const isResend = row.status === 'SENT';
        const canSend = ['DRAFT', 'SENT'].includes(row.status);
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }} onClick={e => e.stopPropagation()}>
            <Tooltip title="Kopier som nyt tilbud">
              <IconButton size="small" onClick={async () => {
                try {
                  const copy = await api.copyQuote(row.id);
                  navigate(`/tilbud/${copy.id}/rediger`);
                } catch (e) {
                  console.error('Kunne ikke kopiere', e);
                }
              }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canSend && (
              <Tooltip title={isResend ? 'Gensend tilbud til kunde' : 'Send tilbud til kunde'}>
                <IconButton
                  size="small"
                  color={isResend ? 'default' : 'primary'}
                  onClick={() => setSendConfirm(row)}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Tilbud</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<ViewKanbanIcon />} onClick={() => navigate('/tilbud/pipeline')}>Pipeline</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tilbud/ny')}>Nyt tilbud</Button>
        </Box>
      </Box>

      {/* Compact KPI strip */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>I pipeline</Typography>
              <Typography variant="h6" fontWeight={700} color="primary" sx={{ lineHeight: 1.2 }}>
                {fmtCurrency(counts.pipelineValue, 'DKK')}
              </Typography>
              <Typography variant="caption" color="text.secondary">{counts.sent} sendte tilbud</Typography>
            </Box>
            {counts.followup > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Kræver opfølgning</Typography>
                <Typography variant="h6" fontWeight={700} color="warning.main" sx={{ lineHeight: 1.2 }}>
                  {counts.followup}
                </Typography>
                <Typography variant="caption" color="text.secondary">tilbud &gt; 7 dage</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Accepteret (afventer)</Typography>
              <Typography variant="h6" fontWeight={700} color="success.main" sx={{ lineHeight: 1.2 }}>
                {counts.accepted}
              </Typography>
              <Typography variant="caption" color="text.secondary">klar til konvertering</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filter chips — combined status filter (no separate followup button) */}
      <Box sx={{ mb: 1.5 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, val) => { if (val) setFilter(val); }}
          size="small"
        >
          <ToggleButton value="all">Alle ({counts.all})</ToggleButton>
          <ToggleButton value="draft">Kladder ({counts.draft})</ToggleButton>
          <ToggleButton value="sent">Sendt ({counts.sent})</ToggleButton>
          {counts.followup > 0 && (
            <ToggleButton value="followup" sx={{ color: 'warning.main !important' }}>
              ⚠ Opfølgning ({counts.followup})
            </ToggleButton>
          )}
          <ToggleButton value="accepted">Accepteret ({counts.accepted})</ToggleButton>
          <ToggleButton value="rejected">Afvist ({counts.rejected})</ToggleButton>
          <ToggleButton value="converted">Konverteret ({counts.converted})</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isLoading ? (
        <Typography>Indlæser...</Typography>
      ) : (
        <DataTable
          columns={columns}
          data={quotes}
          rowKey="id"
          onRowClick={(row) => navigate(`/tilbud/${row.id}`)}
          searchable
          searchPlaceholder="Søg tilbud..."
        />
      )}

      {/* Bekræftigelsesdialog inden der sendes til kunde */}
      <Dialog open={sendConfirm != null} onClose={() => setSendConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {sendConfirm?.status === 'SENT' ? 'Gensend tilbud?' : 'Send tilbud til kunde?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tilbud <strong>#{sendConfirm?.quoteNumber}</strong> sendes til <strong>{sendConfirm?.customerEmail}</strong>
            {' '}med PDF og accept-link. Dette er det <strong>eneste</strong> sted systemet sender mail til kunden.
            {sendConfirm?.status === 'SENT' && (
              <> Der genereres et <strong>nyt accept-link</strong> — gamle links vil ikke længere virke.</>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendConfirm(null)}>Annuller</Button>
          <Button variant="contained" color="primary" onClick={handleSend} disabled={sendQuote.isPending} startIcon={<SendIcon />}>
            {sendQuote.isPending ? <CircularProgress size={18} /> : (sendConfirm?.status === 'SENT' ? 'Gensend' : 'Send nu')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
